// Compare page — season option building for the global "Season" selector.
//
// The stats endpoint mixes two sources with two season-name formats
// (TheSports "2025-2026", SportMonks "2025/2026") and can list the same
// season several times. This module is the single presentation-side fix:
//   - normalise every season name to "2025/26" (single years stay "2025")
//   - the GLOBAL selector offers SEASONS ONLY (Marin follow-up 2026-08-14:
//     "wenn wir vergleichen, vergleichen wir bei allen das gleiche Jahr") —
//     the team an entry belongs to is shown PER PLAYER COLUMN instead
//     ("2025/26 · AC Milan", entryTeamLabel below)
//   - de-dupe identical seasons across players and sources
//   - default = the newest season in which at least one player has a CLUB
//     entry (brief data rule 3: club entries over national-team entries)

/**
 * Normalise a raw season name to "YYYY/YY" (cross-year) or "YYYY" (single).
 * Handles "2025-2026", "2025/2026", "2025-26", "2025/26", "2025" and the
 * reversed "2026/2025" ordering. Unknown formats pass through trimmed.
 */
export const normalizeSeason = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^\d{4}$/.test(s)) return s;
  const m = s.match(/^(\d{4})\s*[/\-–]\s*(\d{2}|\d{4})$/);
  if (!m) return s;
  const a = parseInt(m[1], 10);
  let b = parseInt(m[2], 10);
  if (m[2].length === 2) {
    // expand the short year within a's century, rolling over "1999-00" -> 2000
    b = a - (a % 100) + b;
    if (b < a) b += 100;
  }
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (lo === hi) return String(lo);
  return `${lo}/${String(hi).slice(2)}`;
};

/** Sortable year of a normalised season label (end year of a cross season). */
export const seasonSortKey = (normalized) => {
  if (!normalized) return 0;
  const m = String(normalized).match(/^(\d{4})(?:\/(\d{2}))?$/);
  if (!m) return 0;
  const start = parseInt(m[1], 10);
  if (!m[2]) return start;
  let end = start - (start % 100) + parseInt(m[2], 10);
  if (end < start) end += 100;
  return end;
};

/**
 * Club vs national-team entry. The stats payload carries no explicit flag
 * (views.py stats endpoint: team = {id,name,logo} only), but every entry
 * carries the player's nationality — a national-team entry is the one whose
 * team name STARTS WITH that nationality ("Portugal", "Portugal U21").
 * Entries without a team name count as non-club.
 */
export const isClubEntry = (entry) => {
  const team = entry?.team?.name;
  if (!team) return false;
  const nationality = entry?.player?.nationality;
  if (!nationality) return true;
  return !String(team)
    .trim()
    .toLowerCase()
    .startsWith(String(nationality).trim().toLowerCase());
};

/**
 * Build the GLOBAL selector options from one or many players' stats lists:
 * seasons only, de-duped across players/teams/sources, newest first.
 *
 * @param {Array<Array<object>>} entryLists  stats arrays (one per player)
 * @returns {Array<{id, season, label, sortKey}>}
 */
export const buildSeasonOptions = (entryLists) => {
  const bySeason = new Map();
  (entryLists || []).forEach((entries) => {
    (entries || []).forEach((entry) => {
      const season = normalizeSeason(entry?.season_name);
      if (!season || bySeason.has(season)) return;
      bySeason.set(season, {
        id: season,
        season,
        label: season,
        sortKey: seasonSortKey(season),
      });
    });
  });
  return [...bySeason.values()].sort((a, b) => b.sortKey - a.sortKey);
};

/**
 * Resolve the stats entry ONE player shows for the chosen season.
 * Club entries are preferred over national-team entries (brief data rule 3);
 * within the preferred subset the current-season entry wins, then the
 * TheSports source; null when the player has nothing for that season
 * (-> empty column, frame 1f).
 */
export const resolveEntryForPlayer = (entries, season) => {
  if (!season || !Array.isArray(entries) || !entries.length) return null;
  const sameSeason = entries.filter(
    (e) => normalizeSeason(e?.season_name) === season,
  );
  if (!sameSeason.length) return null;
  const clubs = sameSeason.filter(isClubEntry);
  const pool = clubs.length ? clubs : sameSeason;
  return (
    pool.find((e) => e?.is_current) ||
    pool.find((e) => e?.source === "thesports") ||
    pool[0]
  );
};

/**
 * Per-column label under the player header: "2025/26 · AC Milan" — the season
 * plus the team of THIS player's resolved entry, so it is always visible whose
 * stats come from which team (club vs national side, mid-season move).
 */
export const entryTeamLabel = (season, entry) => {
  if (!season) return null;
  const team = entry?.team?.name;
  return team ? `${season} · ${team}` : season;
};

/**
 * Default selection: the NEWEST season in which at least one player has a
 * club entry; falls back to the newest season overall.
 */
export const defaultSeasonId = (options, entryLists) => {
  if (!options || !options.length) return null;
  const lists = Array.isArray(entryLists) ? entryLists : [];
  const clubSeasons = new Set();
  lists.forEach((entries) => {
    (entries || []).forEach((entry) => {
      if (!isClubEntry(entry)) return;
      const season = normalizeSeason(entry?.season_name);
      if (season) clubSeasons.add(season);
    });
  });
  const withClub = options.find((o) => clubSeasons.has(o.season));
  return (withClub || options[0]).id;
};
